import React, { useState, useEffect, useRef } from 'react';
import { fetchThresholds, updateThresholds, fetchAlertSettings, updateAlertSettings } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './ThresholdModal.css';

const ThresholdModal = ({ siteId, siteName, onClose, onSave }) => {
  const { authData } = useAuth();
  const [thresholds, setThresholds] = useState(null);
  const [alertSettings, setAlertSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Use refs to preserve state across re-renders caused by parent updates
  const loadedSiteIdRef = useRef(null);
  const activeTabRef = useRef('thresholds');
  
  // Initialize activeTab from ref to preserve across re-renders
  const [activeTab, setActiveTab] = useState(() => activeTabRef.current);
  
  // Sync ref with state whenever activeTab changes
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Only load data when siteId changes, not when component re-renders
  useEffect(() => {
    // Only load if siteId actually changed, not on every render
    if (loadedSiteIdRef.current !== siteId) {
      loadedSiteIdRef.current = siteId;
      // Reset activeTab only when siteId changes (new site)
      activeTabRef.current = 'thresholds';
      setActiveTab('thresholds');
      loadData();
    } else {
      // If siteId hasn't changed but component re-rendered, restore activeTab from ref
      // This prevents tab reset when parent component updates due to WebSocket events
      if (activeTabRef.current !== activeTab) {
        setActiveTab(activeTabRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);
  
  // Preserve activeTab across re-renders by using a ref-based approach
  // This ensures the tab doesn't reset when parent component updates
  const handleTabChange = (tab) => {
    activeTabRef.current = tab;
    setActiveTab(tab);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load thresholds and alert settings separately to handle errors gracefully
      let thresholdsData = null;
      let alertSettingsData = null;
      
      try {
        thresholdsData = await fetchThresholds(siteId);
        setThresholds(thresholdsData);
      } catch (err) {
        console.error('Error loading thresholds:', err);
        setError('Failed to load thresholds');
        return;
      }
      
      try {
        alertSettingsData = await fetchAlertSettings(siteId);
        // Ensure loadAverageSchedule is initialized if missing
        if (!alertSettingsData.loadAverageSchedule) {
          alertSettingsData.loadAverageSchedule = {
            monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
          };
        }
        setAlertSettings(alertSettingsData);
      } catch (err) {
        console.error('Error loading alert settings:', err);
        // If alert settings fail (e.g., table doesn't exist), use defaults (all enabled)
        const defaultSchedule = {
          monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
          tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
          wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
          thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
          friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
          saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
          sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
        };
        const defaultSettings = {
          siteId: siteId,
          cpu: true,
          memory: true,
          disk: true,
          responseTime: true,
          loadAverage: true,
          loadAverageSchedule: defaultSchedule,
          heartbeat: true,
          siteIpChanged: true,
          camera: true,
          controller: true,
          myParkPayment: true,
          myparkServer: true,
          slackEnabled: true,
          dbAvailability: false,
          dbAvailabilityWarningDays: 30,
          dbAvailabilityCriticalDays: 5,
          windowsFirewall: true,
          pinpadStatus: true,
          lastPayment: true
        };
        setAlertSettings(defaultSettings);
        // Show a warning but don't block the UI
        console.warn('Using default alert settings (all enabled). If you want to customize alert settings, please run the migration: create_alert_settings_table.sql');
      }
    } catch (err) {
      setError(err.message || 'Failed to load settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleThresholdChange = (category, type, value) => {
    setThresholds(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [type]: parseFloat(value) || 0
      }
    }));
  };

  const handleAlertSettingChange = (metric, enabled) => {
    setAlertSettings(prev => ({
      ...prev,
      [metric]: enabled
    }));
  };

  const handleScheduleDayToggle = (day) => {
    setAlertSettings(prev => {
      const schedule = prev.loadAverageSchedule || {
        monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
      };
      return {
        ...prev,
        loadAverageSchedule: {
          ...schedule,
          [day]: {
            ...schedule[day],
            enabled: !schedule[day].enabled
          }
        }
      };
    });
  };

  const handleTimeSlotChange = (day, slotIndex, field, value) => {
    setAlertSettings(prev => {
      const schedule = prev.loadAverageSchedule || {
        monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
      };
      const newTimeSlots = [...schedule[day].timeSlots];
      newTimeSlots[slotIndex] = {
        ...newTimeSlots[slotIndex],
        [field]: value
      };
      return {
        ...prev,
        loadAverageSchedule: {
          ...schedule,
          [day]: {
            ...schedule[day],
            timeSlots: newTimeSlots
          }
        }
      };
    });
  };

  const addTimeSlot = (day) => {
    setAlertSettings(prev => {
      const schedule = prev.loadAverageSchedule || {
        monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
      };
      return {
        ...prev,
        loadAverageSchedule: {
          ...schedule,
          [day]: {
            ...schedule[day],
            timeSlots: [...schedule[day].timeSlots, { start: "00:00", end: "23:59" }]
          }
        }
      };
    });
  };

  const removeTimeSlot = (day, slotIndex) => {
    setAlertSettings(prev => {
      const schedule = prev.loadAverageSchedule || {
        monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
        sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
      };
      const newTimeSlots = schedule[day].timeSlots.filter((_, idx) => idx !== slotIndex);
      return {
        ...prev,
        loadAverageSchedule: {
          ...schedule,
          [day]: {
            ...schedule[day],
            timeSlots: newTimeSlots.length > 0 ? newTimeSlots : [{ start: "00:00", end: "23:59" }]
          }
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Save based on active tab
      const savePromises = [];
      
      // Save thresholds if on thresholds tab and thresholds are loaded
      if (activeTab === 'thresholds' && thresholds) {
        savePromises.push(updateThresholds(siteId, thresholds));
      }
      
      // Save alert settings if on alerts tab or schedule tab and alert settings are loaded
      if ((activeTab === 'alerts' || activeTab === 'schedule') && alertSettings) {
        // Prepare alert settings for saving (remove siteId if present)
        const settingsToSave = { ...alertSettings };
        delete settingsToSave.siteId;
        // Ensure loadAverageSchedule is included
        if (!settingsToSave.loadAverageSchedule && alertSettings.loadAverage) {
          settingsToSave.loadAverageSchedule = {
            monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
          };
        }
        savePromises.push(updateAlertSettings(siteId, settingsToSave, authData?.fullName || authData?.userName || null));
      }
      
      // If saving from thresholds tab, also save alert settings if they exist
      // (to allow saving both when on thresholds tab)
      if (activeTab === 'thresholds' && alertSettings) {
        const settingsToSave = { ...alertSettings };
        delete settingsToSave.siteId;
        // Ensure loadAverageSchedule is included
        if (!settingsToSave.loadAverageSchedule && alertSettings.loadAverage) {
          settingsToSave.loadAverageSchedule = {
            monday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            tuesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            wednesday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            thursday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            friday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            saturday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] },
            sunday: { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] }
          };
        }
        savePromises.push(updateAlertSettings(siteId, settingsToSave, authData?.fullName || authData?.userName || null));
      }
      
      if (savePromises.length === 0) {
        setError('No settings to save');
        return;
      }
      
      const results = await Promise.all(savePromises);
      
      // Update alert settings from server response if available
      for (const result of results) {
        if (result && result.settings) {
          setAlertSettings(result.settings);
          break; // Use first result that has settings
        }
      }
      
      setSuccess('Settings saved successfully!');
      
      // Don't close immediately, let user see the success message
      // Only close after user explicitly closes or after longer delay
      setTimeout(() => {
        if (onSave) {
          onSave();
        }
        // Don't auto-close, let user close manually
      }, 500);
    } catch (err) {
      let errorMessage = 'Failed to save settings';
      if (err.response) {
        // Server responded with error
        errorMessage = err.response.data?.error || `Server error: ${err.response.status} ${err.response.statusText}`;
        console.error('Server error response:', err.response.status, err.response.data);
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check if the server is running.';
        console.error('No response from server:', err.request);
      } else {
        // Error setting up the request
        errorMessage = err.message || 'Failed to save settings';
        console.error('Request setup error:', err.message);
      }
      setError(errorMessage);
      console.error('Full error object:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadData();
  };

  const getThresholdColor = (category, type) => {
    if (!thresholds) return '#6b7280';
    if (type === 'warning') {
      return '#f59e0b'; // Orange/amber for warnings
    } else if (type === 'critical') {
      return '#ef4444'; // Red for critical
    }
    return '#6b7280';
  };

  if (loading) {
    return (
      <div className="threshold-modal-overlay" onClick={onClose}>
        <div className="threshold-modal" onClick={(e) => e.stopPropagation()}>
          <div className="threshold-modal-loading">Loading thresholds...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="threshold-modal-overlay" onClick={onClose}>
      <div className="threshold-modal" onClick={(e) => e.stopPropagation()}>
        <div className="threshold-modal-header">
          <h2>Metric Settings</h2>
          <button className="threshold-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="threshold-modal-tabs">
          <button
            type="button"
            className={`threshold-tab ${activeTab === 'thresholds' ? 'active' : ''}`}
            onClick={() => handleTabChange('thresholds')}
          >
            Thresholds
          </button>
          <button
            type="button"
            className={`threshold-tab ${activeTab === 'alerts' ? 'active' : ''}`}
            onClick={() => handleTabChange('alerts')}
          >
            <span className="tab-label-full">Alert Settings</span>
            <span className="tab-label-short">Alerts</span>
          </button>
          {alertSettings?.loadAverage && (
            <button
              type="button"
              className={`threshold-tab ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => handleTabChange('schedule')}
            >
              <span className="tab-label-full">Load Average Schedule</span>
              <span className="tab-label-short">Schedule</span>
            </button>
          )}
        </div>

        <div className="threshold-modal-content">
          <div className="threshold-site-info">
            <span className="threshold-site-label">Site:</span>
            <span className="threshold-site-name">{siteName}</span>
          </div>

          {error && <div className="threshold-error-message">{error}</div>}
          {success && <div className="threshold-success-message">{success}</div>}

          {activeTab === 'thresholds' && thresholds && (
            <form onSubmit={handleSubmit} className="threshold-form">
              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('cpu', 'warning') }}></span>
                  CPU Thresholds
                </h3>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning (%)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={thresholds.cpu.warning}
                        onChange={(e) => handleThresholdChange('cpu', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('cpu', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical (%)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={thresholds.cpu.critical}
                        onChange={(e) => handleThresholdChange('cpu', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('cpu', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('memory', 'warning') }}></span>
                  Memory Thresholds
                </h3>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning (%)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={thresholds.memory.warning}
                        onChange={(e) => handleThresholdChange('memory', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('memory', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical (%)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={thresholds.memory.critical}
                        onChange={(e) => handleThresholdChange('memory', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('memory', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('disk', 'warning') }}></span>
                  Disk Space Thresholds
                </h3>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning (%)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={thresholds.disk.warning}
                        onChange={(e) => handleThresholdChange('disk', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('disk', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical (%)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={thresholds.disk.critical}
                        onChange={(e) => handleThresholdChange('disk', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('disk', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('responseTime', 'warning') }}></span>
                  Response Time Thresholds
                </h3>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning (ms)
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={thresholds.responseTime.warning}
                        onChange={(e) => handleThresholdChange('responseTime', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('responseTime', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical (ms)
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={thresholds.responseTime.critical}
                        onChange={(e) => handleThresholdChange('responseTime', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('responseTime', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('loadAverage', 'warning') }}></span>
                  Load Average Thresholds
                </h3>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={thresholds.loadAverage.warning}
                        onChange={(e) => handleThresholdChange('loadAverage', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('loadAverage', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={thresholds.loadAverage.critical}
                        onChange={(e) => handleThresholdChange('loadAverage', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('loadAverage', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('unrecognizedExits', 'warning') }}></span>
                  Exists with No Duration Thresholds
                </h3>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={thresholds.unrecognizedExits?.warning ?? 4}
                        onChange={(e) => handleThresholdChange('unrecognizedExits', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('unrecognizedExits', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={thresholds.unrecognizedExits?.critical ?? 6}
                        onChange={(e) => handleThresholdChange('unrecognizedExits', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('unrecognizedExits', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: getThresholdColor('lastPayment', 'warning') }}></span>
                  Last Payment Thresholds
                </h3>
                <p className="threshold-section-description" style={{ marginBottom: '0.75rem', fontSize: '0.9em', color: '#6b7280' }}>
                  Minutes since last payment. Alerts use the same schedule as Load Average.
                </p>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning (minutes)
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={thresholds.lastPayment?.warning ?? 120}
                        onChange={(e) => handleThresholdChange('lastPayment', 'warning', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('lastPayment', 'warning') }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical (minutes)
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={thresholds.lastPayment?.critical ?? 180}
                        onChange={(e) => handleThresholdChange('lastPayment', 'critical', e.target.value)}
                        style={{ borderLeftColor: getThresholdColor('lastPayment', 'critical') }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-section">
                <h3 className="threshold-section-title">
                  <span className="threshold-indicator" style={{ backgroundColor: '#f59e0b' }}></span>
                  DB Availability Thresholds
                </h3>
                <p className="threshold-section-description" style={{ marginBottom: '0.75rem', fontSize: '0.9em', color: '#6b7280' }}>
                  Days before password expiry: alert when below these values (only applies when DB Availability alerts are enabled in Alert Settings).
                </p>
                <div className="threshold-row">
                  <div className="threshold-input-group">
                    <label>
                      Warning (days)
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="365"
                        value={alertSettings?.dbAvailabilityWarningDays ?? 30}
                        onChange={(e) => setAlertSettings(prev => prev ? {
                          ...prev,
                          dbAvailabilityWarningDays: Math.max(1, parseInt(e.target.value, 10) || 30)
                        } : prev)}
                        style={{ borderLeftColor: '#f59e0b' }}
                      />
                    </label>
                  </div>
                  <div className="threshold-input-group">
                    <label>
                      Critical (days)
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max="365"
                        value={alertSettings?.dbAvailabilityCriticalDays ?? 5}
                        onChange={(e) => setAlertSettings(prev => prev ? {
                          ...prev,
                          dbAvailabilityCriticalDays: Math.max(0, parseInt(e.target.value, 10) || 5)
                        } : prev)}
                        style={{ borderLeftColor: '#ef4444' }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-modal-actions">
                <button type="button" onClick={handleReset} disabled={saving} className="threshold-btn-reset">
                  Reset
                </button>
                <button type="submit" disabled={saving} className="threshold-btn-save">
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'alerts' && alertSettings && (
            <form onSubmit={handleSubmit} className="threshold-form">
              <div className="alert-settings-section">
                <h3 className="alert-settings-title">Enable/Disable Alerts</h3>
                <p className="alert-settings-description">
                  Toggle alerts on or off for each metric type. When disabled, no alerts will be generated for that metric.
                </p>
                
                <div className="alert-settings-grid">
                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>CPU Usage</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.cpu}
                        onChange={(e) => handleAlertSettingChange('cpu', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Memory Usage</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.memory}
                        onChange={(e) => handleAlertSettingChange('memory', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Disk Space</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.disk}
                        onChange={(e) => handleAlertSettingChange('disk', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Response Time</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.responseTime}
                        onChange={(e) => handleAlertSettingChange('responseTime', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Load Average</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.loadAverage}
                        onChange={(e) => handleAlertSettingChange('loadAverage', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Last Payment</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.lastPayment !== false}
                        onChange={(e) => handleAlertSettingChange('lastPayment', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Exists with No Duration</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.unrecognizedExits !== false}
                        onChange={(e) => handleAlertSettingChange('unrecognizedExits', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Heartbeat</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.heartbeat}
                        onChange={(e) => handleAlertSettingChange('heartbeat', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Site IP Changed</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.siteIpChanged !== false}
                        onChange={(e) => handleAlertSettingChange('siteIpChanged', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Cameras</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.camera}
                        onChange={(e) => handleAlertSettingChange('camera', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Controllers</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.controller}
                        onChange={(e) => handleAlertSettingChange('controller', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>MyPark Payment</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.myParkPayment !== false}
                        onChange={(e) => handleAlertSettingChange('myParkPayment', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>MyPark Server</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.myparkServer}
                        onChange={(e) => handleAlertSettingChange('myparkServer', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Pinpad Status</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.pinpadStatus !== false}
                        onChange={(e) => handleAlertSettingChange('pinpadStatus', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>DB Availability</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.dbAvailability === true}
                        onChange={(e) => handleAlertSettingChange('dbAvailability', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Windows Firewall</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.windowsFirewall !== false}
                        onChange={(e) => handleAlertSettingChange('windowsFirewall', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>

                  <div className="alert-setting-item">
                    <label className="alert-setting-label">
                      <span>Slack Notifications</span>
                      <input
                        type="checkbox"
                        checked={alertSettings.slackEnabled !== false}
                        onChange={(e) => handleAlertSettingChange('slackEnabled', e.target.checked)}
                        className="alert-setting-toggle"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="threshold-modal-actions">
                <button type="button" onClick={handleReset} disabled={saving} className="threshold-btn-reset">
                  Reset
                </button>
                <button type="submit" disabled={saving} className="threshold-btn-save">
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'schedule' && alertSettings && alertSettings.loadAverage && (
            <form onSubmit={handleSubmit} className="threshold-form">
              <div className="load-average-schedule-section-full">
                <h3 className="schedule-section-title">Load Average / Last Payment Alerting Schedule</h3>
                <p className="schedule-section-description">
                  Configure when Load Average and Last Payment alerts should be active. Alerts will only be generated during enabled days and time slots.
                </p>
                <div className="schedule-table-container">
                  <table className="schedule-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Enabled</th>
                        <th>Time Slots</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                        const daySchedule = alertSettings.loadAverageSchedule?.[day] || { enabled: true, timeSlots: [{ start: "00:00", end: "23:59" }] };
                        const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
                        return (
                          <tr key={day} className={daySchedule.enabled ? 'schedule-day-enabled' : 'schedule-day-disabled'}>
                            <td className="schedule-day-name">{dayLabel}</td>
                            <td className="schedule-day-toggle">
                              <input
                                type="checkbox"
                                checked={daySchedule.enabled}
                                onChange={() => handleScheduleDayToggle(day)}
                              />
                            </td>
                            <td className="schedule-time-slots-cell">
                              {daySchedule.enabled ? (
                                <div className="schedule-time-slots-compact">
                                  {daySchedule.timeSlots.map((slot, slotIndex) => (
                                    <div key={slotIndex} className="time-slot-compact">
                                      <input
                                        type="time"
                                        value={slot.start}
                                        onChange={(e) => handleTimeSlotChange(day, slotIndex, 'start', e.target.value)}
                                        className="time-input-compact"
                                      />
                                      <span className="time-separator-compact">-</span>
                                      <input
                                        type="time"
                                        value={slot.end}
                                        onChange={(e) => handleTimeSlotChange(day, slotIndex, 'end', e.target.value)}
                                        className="time-input-compact"
                                      />
                                      {daySchedule.timeSlots.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            removeTimeSlot(day, slotIndex);
                                          }}
                                          className="remove-time-slot-btn-compact"
                                          title="Remove time slot"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      addTimeSlot(day);
                                    }}
                                    className="add-time-slot-btn-compact"
                                  >
                                    + Add Time Slot
                                  </button>
                                </div>
                              ) : (
                                <span className="schedule-disabled-text">Disabled</span>
                              )}
                            </td>
                            <td className="schedule-actions-cell">
                              {daySchedule.enabled && daySchedule.timeSlots.length === 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addTimeSlot(day);
                                  }}
                                  className="add-first-slot-btn"
                                >
                                  Add Slot
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="threshold-modal-actions">
                <button type="button" onClick={handleReset} disabled={saving} className="threshold-btn-reset">
                  Reset
                </button>
                <button type="submit" disabled={saving} className="threshold-btn-save">
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThresholdModal;

